import { Button, Input, InputNumber, Select } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import { COVERAGE_LIMIT_CATEGORIES } from "utils/constants";
import { limitUpsellIds } from "utils/helpers/coverageLimits";
import PricingInput from "./PricingInput";

const BenefitRow = ({
  mapping,
  showPricing,
  onRemove,
  onFieldChange,
  onPricerChange,
  onLimitsChange,
  upsellPlans,
  zones,
  ageBrackets,
  regimes,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const { t: benefits } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.COVERAGE_BENEFIT,
  );
  const form = benefits("LIST", { returnObjects: true });
  const benefit = get(mapping, "coverage", {});
  const limits = get(mapping, "limits", []);
  const limitsRef = useRef(limits);
  limitsRef.current = limits;

  const saveField = (field, val) => {
    onFieldChange(field, val);
  };

  const writeLimits = (nextLimits, persist) => {
    limitsRef.current = nextLimits;
    onLimitsChange(nextLimits, persist);
  };

  const updateLimit = (limitIndex, patch, persist) => {
    const nextLimits = limitsRef.current.map((limit, index) =>
      index === limitIndex ? { ...limit, ...patch } : limit,
    );
    writeLimits(nextLimits, persist);
  };

  const categoryOptions = Object.entries(COVERAGE_LIMIT_CATEGORIES).map(
    ([key, value]) => ({
      label: get(form, ["COVERAGE_LIMITS", "CATEGORIES", key], key),
      value,
    }),
  );
  const byPlanName = (left, right) =>
    String(left || "").localeCompare(String(right || ""), "fr", {
      numeric: true,
      sensitivity: "base",
    });
  const upsellOptions = [...(upsellPlans || [])]
    .sort((left, right) => byPlanName(left.name, right.name))
    .map((plan) => ({
      label: plan.name,
      value: plan.id,
    }));
  const nameById = upsellOptions.reduce((names, option) => {
    names[option.value] = option.label;
    return names;
  }, {});
  const sortedUpsellIds = (ids) =>
    [...(ids || [])].sort((left, right) =>
      byPlanName(nameById[left], nameById[right]),
    );

  return (
    <div className="benefit-row">
      <div className="d-flex justify--between benefit-row__header">
        <div>
          <span className="benefit-row__title">{benefit.type}</span>
          {benefit.subtype && (
            <span className="benefit-row__subtitle"> — {benefit.subtype}</span>
          )}
          {benefit.requirement && (
            <span className="benefit-row__subtitle m-left-8">
              ({benefit.requirement})
            </span>
          )}
        </div>
        <Button
          type="text"
          danger
          icon={<BiTrash size={16} />}
          onClick={onRemove}
        />
      </div>

      <div className="benefit-row__fields">
        <div className="benefit-row__field">
          <label>{t("CONFIGURE.BENEFIT_ROW.WAITING_PERIOD")}</label>
          <InputNumber
            size="small"
            min={0}
            value={get(mapping, "carenceDays")}
            onChange={(val) => saveField("carenceDays", val)}
            className="configure-field--sm"
            addonAfter={t("CONFIGURE.REVIEW.DAYS_SUFFIX")}
            placeholder="0"
          />
        </div>
      </div>

      <div className="benefit-row__limits">
        <label>{t("CONFIGURE.BENEFIT_ROW.LIMITS_TITLE")}</label>
        {limits.map((limit, limitIndex) => (
          <div className="benefit-row__limit" key={limit.id || limitIndex}>
            <div className="benefit-row__limit-field">
              <label>{form.LIMIT_VALUE}</label>
              <Input
                value={get(limit, "value.value") || ""}
                onChange={(event) =>
                  updateLimit(
                    limitIndex,
                    {
                      value: {
                        ...get(limit, "value"),
                        value: event.target.value,
                      },
                    },
                    false,
                  )
                }
                onBlur={(event) =>
                  updateLimit(
                    limitIndex,
                    {
                      value: {
                        ...get(limitsRef.current[limitIndex], "value"),
                        value: event.target.value,
                      },
                    },
                    true,
                  )
                }
              />
            </div>
            <div className="benefit-row__limit-field">
              <label>{form.LIMIT_CATEGORY}</label>
              <Select
                allowClear
                options={categoryOptions}
                placeholder={form.LIMIT_CATEGORY}
                value={get(limit, "value.category") || undefined}
                onChange={(category) =>
                  updateLimit(
                    limitIndex,
                    {
                      value: {
                        ...get(limitsRef.current[limitIndex], "value"),
                        category: category || null,
                      },
                    },
                    true,
                  )
                }
              />
            </div>
            <div className="benefit-row__limit-field">
              <label>{form.LIMIT_UPSELL}</label>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                options={upsellOptions}
                placeholder={form.LIMIT_UPSELL_NONE}
                value={sortedUpsellIds(limitUpsellIds(limit))}
                onChange={(upsellIds) =>
                  updateLimit(
                    limitIndex,
                    {
                      upsells: sortedUpsellIds(upsellIds).map((id) => ({
                        id,
                      })),
                    },
                    true,
                  )
                }
              />
            </div>
            <Button
              type="text"
              danger
              icon={<BiTrash size={16} />}
              onClick={() =>
                writeLimits(
                  limitsRef.current.filter((_, index) => index !== limitIndex),
                  true,
                )
              }
            />
          </div>
        ))}
        <Button
          type="dashed"
          icon={<BiPlus size={16} />}
          onClick={() =>
            writeLimits(
              [
                ...limitsRef.current,
                {
                  id: cuid(),
                  value: { value: "", category: null },
                  upsells: [],
                },
              ],
              true,
            )
          }
        >
          {t("CONFIGURE.BENEFIT_ROW.ADD_LIMIT")}
        </Button>
      </div>

      {showPricing && (
        <div className="benefit-row__pricing">
          <label>{t("CONFIGURE.BENEFIT_ROW.PRICING")}</label>
          <PricingInput
            value={get(mapping, "pricer.prices")}
            zones={zones}
            ageBrackets={ageBrackets}
            regimes={regimes}
            onChange={onPricerChange}
          />
        </div>
      )}
    </div>
  );
};

export default BenefitRow;
